import { Container } from '@mui/material'
import fs from 'fs'
import { GetStaticPaths, GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import React from 'react'
import { addApolloState, initializeApollo } from '../../lib/apolloClient'
import { ProductPage } from '../../src/components/Modules'
import NotFound from '../../src/components/Modules/NotFound'
import Layout from '../../src/constants/Layout'
import { AppContextWrapper } from '../../src/context/appContext'
import {
  GET_ADDITIONAL_ADVANCED_PRODUCT_DATA,
  GET_PARENT_CATEGORIES,
  GET_PARENT_CATEGORIES_NON_RECURSIVE,
  GET_PRODUCT_IN_PRODUCT_PAGE,
  GET_PRODUCT_IN_PRODUCT_PAGE_OLD_URL,
  GET_PRODUCT_PATHS,
} from '../../src/operations/queries'
import { APP_SHOP_SETTINGS } from '../../src/operations/queries/_appQueries'
import {
  flattenCategories,
  sortCategories,
} from '../../src/utilityFunctions/categoryUtils'
import { getProfileIndexFromLanguage } from '../../src/utilityFunctions/getProfile'
import { productMetaTags } from '../../src/utilityFunctions/metaUtils/productMetaTags'
import { useShopSettingsInit } from '../../src/utilityFunctions/useShopSettingsInit'
import { unset } from '../../src/utilityFunctions/dataUtils/unset'
import { getProductDataForProductPage } from '../../src/operations/clientMutations/getProductDataForProductPage'

const Product = ({
  productPermalink,
  productData,
  categoriesData,
  shopSettingsData,
  locale,
  locales,
  defaultLocale,
  _nextI18Next,
  ...props
}) => {
  const { t, i18n } = useTranslation(['common'])
  const { initialI18nStore } = _nextI18Next
  const productSlug = t('product-slug')
  const { isFallback } = useRouter()
  //const data = productData.data

  useShopSettingsInit(shopSettingsData.data.findFirstShopSetting)
  const shopSettings = { ...shopSettingsData.data.findFirstShopSetting }


  // ========= Return 404 if no data is found...
  if (!productData || !productData.visible) {
    return (
      <AppContextWrapper locale={locale} shopSettings={shopSettings}>
        <Layout categoriesData={categoriesData} locale={locale}>
          <Container maxWidth="xl">
            <NotFound />
          </Container>
        </Layout>
      </AppContextWrapper>
    )
  }

  if (isFallback) {
    return <div>Falling back...</div>
  }

  // ===== USER AGENT ==== information about incoming user
  // We use this info to determine if its Safari (as it doesnt support webp images)
  let ua
  // if (props.uaString) {
  //   ua = parse(props.uaString)
  // } else {
  //   ua = parse(window.navigator.userAgent)
  // }

  return (
    <>
      {productMetaTags({
        data: productData,
        locale,
        locales,
        shopSettingsData,
        productSlug,
        defaultLocale,
        initialI18nStore,
      })}
      <AppContextWrapper locale={locale} shopSettings={shopSettings}>
        <Layout categoriesData={categoriesData} locale={locale}>
          <ProductPage
            productPermalink={productPermalink}
            productData={productData}
          />
        </Layout>
      </AppContextWrapper>
    </>
  )
}

const language = process.env.DEFAULT_LOCALE

export const getStaticPaths: GetStaticPaths = async () => {
  const apolloClient = initializeApollo()

  const currentProducts = await apolloClient.query({
    query: GET_PRODUCT_PATHS,
    variables: {
      language: language,
    },
  })

  const paths = currentProducts?.data?.findManyProductContainer
    ?.map((product) => {
      if (product.type === 'SIMPLE_PRODUCT') {
        // ----- SIMPLE PRODUCT
        const productProfileIndex: number = getProfileIndexFromLanguage(
          product.single_product.product_profiles,
          language,
        )
        // ==== BUILD FAILS If there is a path with no permalink
        if (
          !product.single_product.product_profiles[productProfileIndex]
            .meta_information.permalink
        ) {
          // --- A product with no permalink
          console.log('Product with no permalink -->', product?.id)
          return null
        } else {
          return {
            params: {
              product:
                product.single_product.product_profiles[productProfileIndex]
                  .meta_information.permalink,
            },
            locale: language,
          }
        }
      } else if (product.type === 'ADVANCED_PRODUCT') {
        // ----- SIMPLE PRODUCT
        const productProfileIndex: number = getProfileIndexFromLanguage(
          product.advanced_product.advanced_product_profiles,
          language,
        )
        // ==== BUILD FAILS If there is a path with no permalink
        if (
          !product.advanced_product.advanced_product_profiles[
            productProfileIndex
          ].meta_information.permalink
        ) {
          // --- A product with no permalink
          console.log('Product with no permalink -->', product?.id)
          return null
        } else {
          return {
            params: {
              product:
                product.advanced_product.advanced_product_profiles[
                  productProfileIndex
                ].meta_information.permalink,
            },
            locale: language,
          }
        }
      } else {
        // UNHANDLED PRODUCT TYPE...
        console.log('Unhandled product type when generating PRODUCT paths...')
      }
    })
    .filter((item) => item != null)

  return {
    paths: paths,
    // paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({
  params,
  locale,
  locales,
  defaultLocale,
}) => {
  const apolloClient = initializeApollo()
  const SHOP_ID = JSON.parse(process.env.NEXT_PUBLIC_SHOP_ID)

  try {
    const productDataFull = await getProductDataForProductPage(params.product)
    const shopSettingsData = await apolloClient.query({
      query: APP_SHOP_SETTINGS,
      variables: {
        SHOP_ID,
        isHomepage: false,
        isProductPage: true,
      },
    })

    const categoriesDataQuery = await apolloClient.query({
      query: GET_PARENT_CATEGORIES_NON_RECURSIVE,
      variables: {
        language: locale,
      },
    })
    let allCategories = [
      ...JSON.parse(JSON.stringify(categoriesDataQuery.data.findManyCategory)),
    ]
    const sortedByMenuOrder = allCategories.sort(
      (a, b) => a.menu_order - b.menu_order,
    )
    const sortedCategories = sortCategories(sortedByMenuOrder)
    // const flatCategories = flattenCategories(sortedCategories)
    let categoriesData = { ...JSON.parse(JSON.stringify(categoriesDataQuery)) }
    categoriesData.data.findManyCategory = sortedCategories
    // ---------------- END  CATEGORY =============

    return addApolloState(apolloClient, {
      props: {
        // uaString: req.headers['user-agent'],
        productPermalink: params.product,
        productData: productDataFull,
        categoriesData,
        shopSettingsData,
        locale,
        locales,
        defaultLocale,
        ...(await serverSideTranslations(locale, ['common'])),
      },
      revalidate: 3600, // In seconds  i hr = 3600 12h5 = 43200
    })
  } catch (e) {
    console.log('Caught errror in product page PROPS ==> ...', e)
    //  params.product
    // -- Write to file
    fs.appendFile(
      'products-errors.txt',
      `Product skipped -> | ${params.product}, \n`,
      function (err) {
        if (err) return console.log(err)
      },
    )

    return { notFound: true }
  }
}
export default Product
